(function (React, View, Text, styles, fmt, PLAN_TYPES, LINE_TYPES, ENERGY_OPTIONS, HEATING_OPTIONS) {
    const el = React.createElement;
    const asRows = value => Array.isArray(value) ? value.filter(Boolean) : [];
    const number = value => Number(value) || 0;
    const rowLoss = row => number(row.UorPsi) * number(row.AorL) * (number(row.Ti) - number(row.Te));
    const typeName = (row, kind = row?.partKind) =>
        row?.bygningsdel || (kind === 'line' ? LINE_TYPES : PLAN_TYPES)[row?.typeKey]?.name || '—';
    const refValue = (row, bType) => {
        const source = (row?.partKind === 'line' ? LINE_TYPES : PLAN_TYPES)[row?.typeKey] || {};
        return source[bType === 'sommer' ? 'UdefaultSummer' : 'Udefault'] ?? source.Udefault ?? number(row?.UorPsi);
    };
    const sumLoss = rows => rows.reduce((sum, row) => sum + rowLoss(row), 0);
    const isGlass = name => /^(vinduer|ovenlysvinduer?|ovenlyskupler?|yderdøre?\s*med\s*glas)$/i.test(name);
    const pick = (list, key) => list.find((item, index) =>
        item.id === key || String(item.id) === String(key) || String(index) === String(key)
    ) || {};

    function Heading({ children }) {
        return <Text style={[styles.h2, { fontSize: 11, marginTop: 9, marginBottom: 4 }]}>{children}</Text>;
    }

    function KeyValue({ label, value }) {
        return (
            <View style={styles.kvRow}>
                <Text style={styles.kvKey}>{label}</Text>
                <Text style={[styles.kvVal, { fontSize: 10 }]}>{value}</Text>
            </View>
        );
    }

    function Table({ headers, rows, widths }) {
        return (
            <View style={[styles.table, { marginBottom: 5 }]}>
                <View style={[styles.row, styles.head]}>
                    {headers.map((header, index) => (
                        <Text key={index} style={[styles.cell, { flex: widths?.[index] || 1 }]}>{header}</Text>
                    ))}
                </View>
                {rows.map((row, rowIndex) => (
                    <View key={rowIndex} style={styles.row} wrap={false}>
                        {row.map((value, index) => (
                            <Text key={index} style={[styles.cell, { flex: widths?.[index] || 1 }]}>{value}</Text>
                        ))}
                    </View>
                ))}
            </View>
        );
    }

    function VarmetabRows({ title, planRows, lineRows, bType, reference = false }) {
        const renderRows = rows => rows.map(row => {
            const coefficient = reference ? refValue(row, bType) : number(row.UorPsi);
            const loss = coefficient * number(row.AorL) * (number(row.Ti) - number(row.Te));
            return [
                typeName(row),
                fmt(coefficient, 3),
                fmt(number(row.AorL), 2),
                `${fmt(number(row.Ti), 1)} / ${fmt(number(row.Te), 1)}`,
                fmt(loss, 1),
            ];
        });
        return (
            <View>
                <Heading>{title}</Heading>
                {!!planRows.length && (
                    <Table
                        headers={['Bygningsdel', 'U-værdi', 'Areal [m²]', 'Ti / Te [°C]', 'Varmetab [W]']}
                        rows={renderRows(planRows)}
                        widths={[2.2, 1, 1, 1.2, 1]}
                    />
                )}
                {!!lineRows.length && (
                    <Table
                        headers={['Linjetab', 'Ψ-værdi', 'Længde [m]', 'Ti / Te [°C]', 'Varmetab [W]']}
                        rows={renderRows(lineRows)}
                        widths={[2.2, 1, 1, 1.2, 1]}
                    />
                )}
            </View>
        );
    }

    function VarmetabSection({ data }) {
        if (!data) return null;
        const info = data.info || {};
        const bType = info.bType === 'sommer' ? 'sommer' : 'helårs';
        const projectType = info.projectType || 'tilbygning';
        const hasInput = row => number(row.UorPsi) !== 0 || number(row.AorL) !== 0 || !!row.comment;
        const designPlan = asRows(data.designPlan).filter(row =>
            !String(row.id || '').startsWith('__extra') && (hasInput(row) || !!row.bygningsdel)
        );
        const designLine = asRows(data.designLine).filter(hasInput);
        const coverPlan = asRows(data.coverPlan);
        const design = sumLoss([...designPlan, ...designLine]);
        const referencePlan = designPlan.map(row => ({ ...row, UorPsi: refValue(row, bType) }));
        const referenceLine = designLine.map(row => ({ ...row, UorPsi: refValue(row, bType) }));
        const deductions = coverPlan.filter(row => number(row.AorL) > 0 && number(row.UorPsi) > 0);
        const deduction = sumLoss(deductions.map(row => ({ ...row, AorL: number(row.AorL) * 0.5 })));
        const floorArea = parseFloat(info.floorArea);
        const glassArea = designPlan.reduce((sum, row) => sum + (isGlass(typeName(row, 'plan')) ? number(row.AorL) : 0), 0);
        const glassLimit = Number.isFinite(floorArea) ? floorArea * (bType === 'sommer' ? 0.3 : 0.22) : 0;
        const extraGlass = Math.max(0, glassArea - glassLimit);
        const glassFraction = Number.isFinite(floorArea) && floorArea > 0 ? glassArea / floorArea * 100 : 0;
        const glassType = designPlan.find(row => isGlass(typeName(row, 'plan')))
            || { partKind: 'plan', typeKey: PLAN_TYPES.findIndex(item => isGlass(item.name)), Ti: 20, Te: -12 };
        const wallType = designPlan.find(row => /^ydervæg$/i.test(typeName(row, 'plan')))
            || { partKind: 'plan', typeKey: PLAN_TYPES.findIndex(item => /^ydervæg$/i.test(item.name)), Ti: 20, Te: -12 };
        const glassAdjustments = extraGlass > 0 ? [
            {
                ...glassType,
                bygningsdel: 'Justering: overskydende glas (−A)',
                UorPsi: refValue(glassType, bType),
                AorL: -extraGlass,
            },
            {
                ...wallType,
                bygningsdel: 'Justering: regnes som ydervæg (+A)',
                UorPsi: refValue(wallType, bType),
                AorL: extraGlass,
            },
        ] : [];
        const reference = sumLoss([...referencePlan, ...referenceLine, ...glassAdjustments]) + deduction;
        const difference = design - reference;
        const differencePct = reference ? difference / reference * 100 : 0;

        return (
            <View>
                <Text style={styles.h2}>Varmetabs-ramme</Text>
                <Heading>Bygningsinformation</Heading>
                <KeyValue label="Bygningstype" value={bType === 'sommer' ? 'Sommerhus' : 'Helårshus'} />
                <KeyValue label="Projekttype" value={projectType === 'tilbygning' ? 'Tilbygning' : 'Renovering'} />
                <KeyValue label="Etageareal" value={Number.isFinite(floorArea) ? `${fmt(floorArea, 1)} m²` : '—'} />
                {!!info.note && <><Heading>Beskrivelse</Heading><Text style={styles.p}>{String(info.note)}</Text></>}

                <VarmetabRows
                    title={projectType === 'tilbygning' ? 'Tilbygning (ny del) – Bygningsdele (design)' : 'Bygningsdele (design)'}
                    planRows={designPlan}
                    lineRows={designLine}
                    bType={bType}
                />
                <KeyValue label="Varmetab (design)" value={`${fmt(design, 1)} W`} />

                {projectType === 'tilbygning' && (
                    <>
                        <Heading>Glasnøgletal</Heading>
                        <KeyValue label="Glasareal" value={`${fmt(glassArea, 2)} m²`} />
                        <KeyValue label="Andel af etageareal" value={`${fmt(glassFraction, 1)} %`} />
                        <KeyValue label="Tilladt glasareal" value={`${fmt(glassLimit, 2)} m²`} />
                        <KeyValue label="Overskydende glas" value={`${fmt(extraGlass, 2)} m²`} />

                        <Heading>Fradrag (tilbygning)</Heading>
                        {deductions.length ? (
                            <Table
                                headers={['Bygningsdel', 'U-værdi', 'Dækket areal [m²]', 'Fradrag (50 %) [W]']}
                                rows={deductions.map(row => [
                                    typeName(row, 'plan'),
                                    fmt(number(row.UorPsi), 3),
                                    fmt(number(row.AorL), 2),
                                    fmt(rowLoss({ ...row, AorL: number(row.AorL) * 0.5 }), 1),
                                ])}
                                widths={[2.2, 1, 1.2, 1.2]}
                            />
                        ) : <Text style={styles.p}>Ingen fradrag indtastet.</Text>}

                        <Heading>Reference (tilbygning)</Heading>
                        <VarmetabRows
                            title="Bygningsdele (reference)"
                            planRows={[...referencePlan, ...glassAdjustments]}
                            lineRows={referenceLine}
                            bType={bType}
                            reference
                        />
                        <KeyValue label="Varmetab (reference)" value={`${fmt(reference, 1)} W`} />

                        <Heading>Resultatoversigt</Heading>
                        <Table
                            headers={['Reference [W]', 'Design [W]', 'Afvigelse [W]', 'Afvigelse [%]']}
                            rows={[[
                                fmt(reference, 1),
                                fmt(design, 1),
                                `${difference >= 0 ? '+' : ''}${fmt(difference, 1)}`,
                                `${differencePct >= 0 ? '+' : ''}${fmt(differencePct, 1)}`,
                            ]]}
                        />
                    </>
                )}
            </View>
        );
    }

    function scenarioResult(scenario) {
        const heat = pick(HEATING_OPTIONS, scenario.heatIdx);
        const fuel = pick(ENERGY_OPTIONS, scenario.fuelIdx);
        const efficiency = heat.efficiency ?? 1;
        const energyContent = fuel.energyContent ?? 1;
        const degreeDays = scenario.mode === 'single' ? scenario.degreeDays ?? 3037 : scenario.buildingHDD ?? 3037;
        const rows = scenario.mode === 'single'
            ? [{
                name: 'Konstruktion',
                uBefore: scenario.single?.uBefore,
                uAfter: scenario.single?.uAfter,
                area: scenario.single?.areaOne,
            }]
            : asRows(scenario.rows);
        const kWh = Number.isFinite(scenario.kWhSaved) ? scenario.kWhSaved : rows.reduce((sum, row) => {
            const deltaU = Math.max(0, number(row.uBefore) - number(row.uAfter));
            return sum + deltaU * number(row.area) * degreeDays * 24 / 1000;
        }, 0);
        const annualUnits = Number.isFinite(scenario.annualUnits)
            ? scenario.annualUnits : (kWh ? kWh * energyContent / efficiency : 0);
        const annualCost = Number.isFinite(scenario.annualCost)
            ? scenario.annualCost : annualUnits * number(scenario.price);
        return { heat, fuel, efficiency, energyContent, degreeDays, rows, kWh, annualUnits, annualCost };
    }

    function SavingsSection({ data }) {
        if (!data) return null;
        const scenarios = Array.isArray(data) ? data : [data];
        return (
            <View>
                <Text style={styles.h2}>Årlig besparelse</Text>
                {scenarios.map((scenario, index) => {
                    const result = scenarioResult(scenario);
                    return (
                        <View key={scenario.id || index}>
                            <Heading>{scenario.name || `Scenarie ${index + 1}`}</Heading>
                            {!!scenario.note && <><Heading>Beskrivelse</Heading><Text style={styles.p}>{String(scenario.note)}</Text></>}

                            <Heading>Konstruktioner</Heading>
                            <KeyValue label="Graddage" value={fmt(result.degreeDays, 0)} />
                            <Table
                                headers={['Navn', 'U-før [W/m²K]', 'U-efter [W/m²K]', 'Areal [m²]', 'Besparelse [kWh/år]']}
                                rows={result.rows.map(row => {
                                    const rowKWh = Math.max(0, number(row.uBefore) - number(row.uAfter))
                                        * number(row.area) * result.degreeDays * 24 / 1000;
                                    return [
                                        row.name || 'Konstruktion',
                                        fmt(number(row.uBefore), 3),
                                        fmt(number(row.uAfter), 3),
                                        fmt(number(row.area), 1),
                                        fmt(rowKWh, 1),
                                    ];
                                })}
                                widths={[2, 1, 1, 1, 1.2]}
                            />

                            <Heading>Varmeanlæg og pris</Heading>
                            <KeyValue label="Varmeanlæg" value={result.heat.description || '—'} />
                            <KeyValue label="Virkningsgrad" value={fmt(result.efficiency, 2)} />
                            <KeyValue label="Brændsel" value={result.fuel.name || scenario.fuelName || '—'} />
                            <KeyValue
                                label="Pris"
                                value={`${fmt(number(scenario.price), 2)} kr${result.fuel.unit ? `/${result.fuel.unit}` : ''}`}
                            />

                            <Heading>Resultat</Heading>
                            <KeyValue label="Energibesparelse" value={`${fmt(result.kWh, 1)} kWh/år`} />
                            <KeyValue
                                label="Brændselsbesparelse"
                                value={`${fmt(result.annualUnits, 1)} ${result.fuel.unit || scenario.unit || ''}`.trim()}
                            />
                            <KeyValue label="Årlig besparelse" value={`${fmt(result.annualCost, 1)} kr/år`} />
                        </View>
                    );
                })}
            </View>
        );
    }

    return { VarmetabSection, SavingsSection };
})
